import { type ILayoutColumn } from "@/interfaces/component/layout-column.interface"
import { BaseModel } from "@/models/base.model"
import { LayoutColumnComponents } from "@/enums/models/layout-column-components.enum"
import moment from "moment"

export class LayoutColumn extends BaseModel implements ILayoutColumn {
  id?: string

  component: LayoutColumnComponents

  active?: boolean

  props?: {
    [key: string]: any
  }

  constructor(layoutColumn: ILayoutColumn, props?: { [key: string]: any }) {
    super(layoutColumn)

    this.id = layoutColumn.id
    this.component = layoutColumn.component
    this.active = layoutColumn.active
    this.props = props
  }

  /**
   * Components that are UI-only / dev-only and must never be persisted to the backend
   * (the backend has no enum value for them). Filtered out before saving user settings.
   */
  static getNonPersistedComponents(): LayoutColumnComponents[] {
    return [
      LayoutColumnComponents.LAYOUT_CONTROLS,
      LayoutColumnComponents.DEV_TOOLS,
      LayoutColumnComponents.IMPERSONATION
    ]
  }

  static getPickerComponents(): LayoutColumnComponents[] {
    return [
      LayoutColumnComponents.ORDER_LIST,
      LayoutColumnComponents.INSTRUCTION_LIST,
      LayoutColumnComponents.FILE_VIEWER,
      LayoutColumnComponents.MAP,
      LayoutColumnComponents.APP,
      LayoutColumnComponents.INVOICE,
      LayoutColumnComponents.PRODUCTION_STATEMENT,
      LayoutColumnComponents.INSTRUCTION_FINDER,
      LayoutColumnComponents.QUOTE,
      LayoutColumnComponents.WORKSPACE,
      LayoutColumnComponents.DASHBOARD,
      LayoutColumnComponents.DEV_TOOLS
    ]
  }

  static isSupportedComponent(component: LayoutColumnComponents): boolean {
    return [
      ...LayoutColumn.getPickerComponents(),
      LayoutColumnComponents.LAYOUT_CONTROLS,
      LayoutColumnComponents.IMPERSONATION
    ].includes(component)
  }

  getName() {
    switch (this.component) {
      case LayoutColumnComponents.IMPERSONATION:
        return this.props?.label ? `${this.props.label}` : "Impersonatie"
      case LayoutColumnComponents.DEV_TOOLS:
        return "Dev tools"
      case LayoutColumnComponents.LAYOUT_CONTROLS:
        return "Nieuwe kolom"
      case LayoutColumnComponents.ORDER_LIST:
        return "Bestellingen"
      case LayoutColumnComponents.INSTRUCTION_LIST:
        return "Instructies"
      case LayoutColumnComponents.FILE_VIEWER:
        return "Bestanden"
      case LayoutColumnComponents.MAP:
        return "Kaart"
      case LayoutColumnComponents.APP:
        return "App"
      case LayoutColumnComponents.PRODUCTION_STATEMENT:
        return "Productieverantwoording"
      case LayoutColumnComponents.INSTRUCTION_FINDER:
        return "Instructievinder"
      case LayoutColumnComponents.INVOICE:
        return "Inkoopfacturen"
      case LayoutColumnComponents.QUOTE:
        return "Offerte"
      case LayoutColumnComponents.WORKSPACE:
        return "Notities"
      case LayoutColumnComponents.DASHBOARD:
        return "Dashboard"
    }
  }

  // Returns lines with a preferred breakpoint.
  getLabelLines(): string {
    switch (this.component) {
      case LayoutColumnComponents.INVOICE:
        return "Inkoop<wbr>facturen"
      case LayoutColumnComponents.PRODUCTION_STATEMENT:
        return "Productie<wbr>verantwoording"
      default:
        return this.getName()
    }
  }

  getIcon(): [string, string] {
    switch (this.component) {
      case LayoutColumnComponents.IMPERSONATION:
        return ["fas", "people-arrows"]
      case LayoutColumnComponents.DEV_TOOLS:
        return ["fas", "bug"]
      case LayoutColumnComponents.LAYOUT_CONTROLS:
        return ["fas", "table-columns"]
      case LayoutColumnComponents.ORDER_LIST:
        return ["fas", "list-check"]
      case LayoutColumnComponents.INSTRUCTION_LIST:
        return ["fas", "file-lines"]
      case LayoutColumnComponents.FILE_VIEWER:
        return ["fas", "folder-open"]
      case LayoutColumnComponents.MAP:
        return ["fas", "map"]
      case LayoutColumnComponents.APP:
        return ["fas", "mobile-screen"]
      case LayoutColumnComponents.PRODUCTION_STATEMENT:
        return ["fas", "chart-bar"]
      case LayoutColumnComponents.INSTRUCTION_FINDER:
        return ["fas", "magnifying-glass"]
      case LayoutColumnComponents.INVOICE:
        return ["fas", "file-invoice"]
      case LayoutColumnComponents.QUOTE:
        return ["fas", "file-pen"]
      case LayoutColumnComponents.WORKSPACE:
        return ["fas", "note-sticky"]
      case LayoutColumnComponents.DASHBOARD:
        return ["fas", "chart-column"]
      default:
        return ["fas", "table-columns"]
    }
  }

  isSingleInstance(): boolean {
    switch (this.component) {
      case LayoutColumnComponents.DEV_TOOLS:
      case LayoutColumnComponents.ORDER_LIST:
      case LayoutColumnComponents.MAP:
      case LayoutColumnComponents.APP:
      case LayoutColumnComponents.INVOICE:
      case LayoutColumnComponents.PRODUCTION_STATEMENT:
      case LayoutColumnComponents.QUOTE:
      case LayoutColumnComponents.WORKSPACE:
        return true
      default:
        return false
    }
  }

  getProps(): Record<string, any> {
    let props = {}
    switch (this.component) {
      case LayoutColumnComponents.FILE_VIEWER:
        props = {
          autoStretchToBottom: true,
          color: "primary"
        }
        break
      case LayoutColumnComponents.INSTRUCTION_LIST:
        props = {
          // Important to instantiate as a date due to http date serialization (._f property).
          date: moment(moment().format("YYYY-MM-DD"), "YYYY-MM-DD")
        }
        break
    }
    // Deep merging clones contentOverride, so Vue remounts the preview on every
    // column update and loses its scroll position. Keep prop references intact.
    return { ...props, ...this.props }
  }
}

export { LayoutColumnComponents }
